import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfigService } from '../../appConfig.service';
import {
  BankFeedAccount,
  BankFeedAvailability,
  BankFeedImportResult,
  BankFeedPreview,
  BankFeedSession,
  ImportRunPage,
  ImportRunTransactionList
} from '../model/bank-feed';
import { HttpInternalService } from './http.service';

declare const Stripe: any;

/**
 * SD-1207: bank feeds via Stripe Financial Connections, and the import history that
 * covers the Xero and CSV paths as well.
 *
 * Talks to the api/bankfeeds controller, which uses a literal route rather than the older
 * api/[controller]/[action] convention -- hence the lowercase paths. Paths omit the
 * leading "api/" because HttpInternalService prepends AppConfigService.apiURL.
 *
 * That controller returns JsonModel, so success is `status === 1` -- note this is the
 * opposite of the CommonServices.ReturnJson endpoints (Organisation/*, Integration/*),
 * where 0 means success. Both conventions exist in this codebase.
 */
@Injectable({
  providedIn: 'root'
})
export class BankFeedsService {

  constructor(
    private _httpService: HttpInternalService,
    private _configService: AppConfigService
  ) { }

  /**
   * The name an account created from this feed would get, so "Create account" can say what
   * it will make. Mirrors BankFeedImporter.SuggestAccountName on the server, which is what
   * actually decides.
   */
  static suggestedAccountName(feed: BankFeedAccount): string {
    const name = (feed && (feed.displayName || feed.institutionName) || '').trim() || 'Bank account';
    const last4 = (feed && feed.last4 || '').trim();
    return last4 && name.indexOf(last4) < 0 ? `${name} ••${last4}` : name;
  }

  /**
   * Whether this organisation can use bank feeds. Used to show a DISABLED tile with the
   * reason on it rather than hiding the feature -- "why can't I see bank feeds" is a
   * support ticket, and "only available for US organisations" is an answer.
   */
  getAvailability(organisationId: string): Observable<BankFeedAvailability> {
    return this._httpService
      .getRequest<any>('bankfeeds/availability', { organisationId })
      .pipe(map(response => this.unwrap<BankFeedAvailability>(response)));
  }

  getAccounts(organisationId: string): Observable<BankFeedAccount[]> {
    return this._httpService
      .getRequest<any>('bankfeeds/accounts', { organisationId })
      .pipe(map(response => this.unwrap<BankFeedAccount[]>(response) || []));
  }

  /**
   * Runs the whole connect flow: open a session, hand it to Stripe.js, then tell the
   * server what was linked.
   *
   * Resolves to the organisation's feeds, or null when the user closed Stripe's modal
   * without linking anything -- which is not an error and must not be shown as one.
   *
   * `relinkConnectionId` repairs a feed Stripe has marked inactive instead of linking a
   * new one. Stripe relinks against the authorization rather than the account, so one
   * re-authentication can repair every account at that institution.
   */
  async connect(organisationId: string, relinkConnectionId?: string): Promise<BankFeedAccount[] | null> {
    const publishableKey = this._configService.stripePublishableKey;

    // Not just "is it set". A placeholder pasted out of the setup instructions -- the
    // literal "pk_test_…" -- is a non-empty string, so a truthy check passes it straight to
    // Stripe.js, which fails somewhere inside its own bundle. Whoever sees this is an
    // operator looking for a setting to change, so name the setting. Mirrors
    // StripeKeyFormat on the server.
    if (!BankFeedsService.looksLikePublishableKey(publishableKey)) {
      throw new Error(publishableKey
        ? 'Stripe--PublishableKey does not look like a real publishable key — check it was pasted in full.'
        : 'Bank feeds are not configured on this environment.');
    }
    if (typeof Stripe === 'undefined') {
      // js.stripe.com is loaded from index.html; an ad blocker or an offline tab is the
      // usual cause, and a clear message beats "Stripe is not defined".
      throw new Error('We could not load Stripe. Check that your browser is not blocking js.stripe.com.');
    }

    const session = await this.createSession(organisationId, relinkConnectionId).toPromise();
    if (!session || !session.clientSecret) {
      throw new Error('We could not start the bank connection.');
    }

    const stripe = Stripe(publishableKey);
    const result = await stripe.collectFinancialConnectionsAccounts({
      clientSecret: session.clientSecret
    });

    if (result && result.error) {
      throw new Error(result.error.message || 'We could not connect that bank.');
    }

    const linked = result
      && result.financialConnectionsSession
      && result.financialConnectionsSession.accounts;

    // An empty accounts array means the user exited the flow early or linked nothing.
    if (!linked || !linked.length) {
      return null;
    }

    // Read the accounts back from Stripe server-side rather than trusting what the
    // browser reports it linked.
    return this.completeSession(organisationId, session.sessionId).toPromise();
  }

  createSession(organisationId: string, relinkConnectionId?: string): Observable<BankFeedSession> {
    return this._httpService
      .postRequest<any>('bankfeeds/session', { organisationId, relinkConnectionId })
      .pipe(map(response => this.unwrap<BankFeedSession>(response)));
  }

  completeSession(organisationId: string, sessionId: string): Observable<BankFeedAccount[]> {
    return this._httpService
      .postRequest<any>('bankfeeds/session/complete', { organisationId, sessionId })
      .pipe(map(response => this.unwrap<BankFeedAccount[]>(response) || []));
  }

  /**
   * Creates a ledger account from the feed's own details and links the two, for a bank that
   * has no counterpart in Synced yet.
   *
   * One call rather than create-then-map: they are one intent, and splitting them leaves an
   * orphaned account behind whenever the second half fails. The server refuses this for an
   * organisation with an accounting system, whose accounts are not ours to add to.
   */
  createAndMapAccount(organisationId: string, connectionId: string, accountName?: string):
    Observable<BankFeedAccount> {

    return this._httpService
      .postRequest<any>('bankfeeds/accounts/create-and-map', { organisationId, connectionId, accountName })
      .pipe(map(response => this.unwrap<BankFeedAccount>(response)));
  }

  /** Binds a feed to a ledger account and turns on Stripe's daily refresh. */
  mapAccount(organisationId: string, connectionId: string, accountId: string): Observable<BankFeedAccount> {
    return this._httpService
      .postRequest<any>('bankfeeds/accounts/map', { organisationId, connectionId, accountId })
      .pipe(map(response => this.unwrap<BankFeedAccount>(response)));
  }

  /**
   * The historical import. `fromDate` is clamped server-side to what Stripe actually
   * holds (about 180 days, institution-dependent), so asking for more is not an error --
   * the run records the period actually covered.
   */
  importHistory(organisationId: string, connectionId: string, fromDate: Date | null,
                transactionIds?: string[], reportId?: string):
    Observable<BankFeedImportResult> {

    return this._httpService
      .postRequest<any>('bankfeeds/accounts/import', {
        organisationId,
        connectionId,
        fromDate: fromDate ? BankFeedsService.toIsoDate(fromDate) : null,
        // Omitted, the server imports everything new -- the unreviewed behaviour.
        transactionIds: transactionIds || null,
        reportId: reportId || null
      })
      .pipe(map(response => this.unwrap<BankFeedImportResult>(response)));
  }

  /** What importHistory would bring in, without writing anything: the review step. */
  previewHistory(organisationId: string, connectionId: string, fromDate: Date | null):
    Observable<BankFeedPreview> {

    return this._httpService
      .postRequest<any>('bankfeeds/accounts/preview', {
        organisationId,
        connectionId,
        fromDate: fromDate ? BankFeedsService.toIsoDate(fromDate) : null
      })
      .pipe(map(response => this.unwrap<BankFeedPreview>(response)));
  }

  /** Pull now. Omitting connectionId refreshes every mapped, active feed. */
  refresh(organisationId: string, connectionId?: string): Observable<BankFeedImportResult[]> {
    return this._httpService
      .postRequest<any>('bankfeeds/accounts/refresh', { organisationId, connectionId })
      .pipe(map(response => this.unwrap<BankFeedImportResult[]>(response) || []));
  }

  /**
   * Disconnects a feed. The transactions it already imported stay in the ledger -- they
   * are the organisation's bookkeeping, not ours to withdraw.
   */
  disconnect(organisationId: string, connectionId: string): Observable<any> {
    return this._httpService.deleteRequest<any>(
      `bankfeeds/accounts/${connectionId}`, { organisationId });
  }

  /**
   * The import history. Covers the Stripe feed, the Xero pull and CSV uploads alike, and
   * is not gated on the bank-feeds module for that reason.
   */
  /**
   * The organisation's import history. `accountId` narrows it to one ledger account, which
   * is what Settings > Banking shows in a feed's sync log.
   */
  getImports(organisationId: string, page = 1, pageSize = 20, search = '', source = '', accountId = ''):
    Observable<ImportRunPage> {

    const params: any = { organisationId, page: String(page), pageSize: String(pageSize) };
    if (search && search.trim()) { params.search = search.trim(); }
    if (source) { params.source = source; }
    if (accountId) { params.accountId = accountId; }

    return this._httpService
      .getRequest<any>('bankfeeds/imports', params)
      .pipe(map(response => ({
        runs: (response && response.data) || [],
        total: (response && response.otherData && response.otherData.total) || 0
      })));
  }

  /** What one import run did, transaction by transaction (recorded for runs since 19 Oct 2026). */
  getImportTransactions(organisationId: string, runId: string): Observable<ImportRunTransactionList> {
    return this._httpService
      .getRequest<any>(`bankfeeds/imports/${encodeURIComponent(runId)}/transactions`, { organisationId })
      .pipe(map(response => ({
        transactions: (response && response.data) || [],
        total: (response && response.otherData && response.otherData.total) || 0
      })));
  }

  /**
   * Right prefix, long enough to be real, and made only of the characters Stripe uses. An
   * ellipsis, a space or a stray quote means the value came from a document rather than from
   * Stripe. Deliberately not a full-format regex: rejecting a valid key would take a working
   * bank feed offline, which is worse than accepting a bad one.
   */
  private static looksLikePublishableKey(value: string): boolean {
    if (!value) { return false; }
    const trimmed = value.trim();
    return trimmed.startsWith('pk_') && trimmed.length >= 20 && /^[A-Za-z0-9_-]+$/.test(trimmed);
  }

  /**
   * One sentence for a failed bank-feed call.
   *
   * Every one of these screens used to collapse an HTTP failure into a single sentence --
   * "We could not check whether bank feeds are available." -- which is true of a 403, a 404,
   * an expired session and a backend that is not running. All four have different fixes and
   * three of them are configuration rather than code, so the one thing the message must not
   * do is hide which happened. Both the status and a plain-English cause go in, so a
   * screenshot is enough to say what to do next.
   *
   * Order matters: a message the server wrote for this specific case always beats a generic
   * one derived from the status code.
   */
  static describeError(error: any, fallback: string): string {
    // unwrap() throws a real Error carrying the JsonModel Message. HttpErrorResponse is not
    // an Error, so this branch is only ever our own handled-failure message.
    if (error instanceof Error && error.message) { return error.message; }
    if (!error) { return fallback; }

    const body = typeof error.error === 'string'
      ? BankFeedsService.safeParse(error.error)
      : error.error;

    // Dual-cased, unlike unwrap() below: an error body is not always a JsonModel -- it can
    // be a bare object from a filter, or something a proxy wrote. Same idiom as the house
    // helper in shared/helpers/api-error.ts.
    if (body && (body.Message || body.message)) { return body.Message || body.message; }

    // The RequireModule filter's machine-readable 403. Worth naming precisely: it means the
    // module exists but is not granted to this organisation, which is an admin action, not
    // a bug -- and in Pilot rollout it is the expected answer until an override is added.
    if (body && body.error === 'module_not_entitled') {
      return 'Bank feeds are not switched on for this organisation yet (403).';
    }

    switch (error.status) {
      case 0:
        return 'We could not reach the API. Check that it is running and that the app is pointed at it.';
      case 401:
        return 'Your session has expired. Sign in again.';
      case 403:
        return 'You do not have access to bank feeds here (403).';
      case 404:
        return 'This API does not have bank feeds (404) — it may be running an older build.';
      default:
        break;
    }

    if (error.status >= 500) {
      return `The API could not answer (${error.status}). Check its log — a bank-feed migration may not have been applied.`;
    }

    return fallback;
  }

  private static safeParse(value: string): any {
    try { return JSON.parse(value); } catch { return null; }
  }

  /**
   * JsonModel carries the payload in `data` and signals a handled failure with status 0 and
   * a message written for the user, on an HTTP 200. Turning that into a thrown error here
   * means every caller can use one error path instead of checking a status code.
   *
   * The properties are lowercase and that is not a typo. Synced/Models/JsonModel.cs declares
   * them `Data` / `Message` / `OtherData` with no [JsonPropertyName], and Startup.cs's
   * AddJsonOptions adds two converters without ever setting PropertyNamingPolicy -- so
   * JsonSerializerDefaults.Web applies and every property ships camelCase. `status` is the
   * one field that looks untouched, because it is already lowercase in the C# class.
   *
   * This service read `response.Data` for its first four commits. That is `undefined` against
   * the real API, so every call resolved to undefined and each caller rendered its own "we
   * could not..." default -- a feature that could not work in a browser while its tests were
   * green, because the tests mocked the same PascalCase that does not exist. Do not "correct"
   * this back.
   */
  private unwrap<T>(response: any): T {
    if (!response) { return null; }
    if (response.status === 0) {
      throw new Error(response.message || 'Something went wrong.');
    }
    return response.data as T;
  }

  /**
   * Local date as yyyy-MM-dd. Deliberately not toISOString(), which converts to UTC and
   * can move a date across a day boundary -- picking "1 Sep" in Sydney would query from
   * 31 Aug. Same helper, same reason, as TransactionsService.
   */
  private static toIsoDate(value: Date): string {
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }
}
